import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { onValue, ref, getDatabase, Database } from "firebase/database";
import { signInWithCustomToken, getAuth, Auth } from "firebase/auth";
import {
  deleteApp,
  getApp,
  getApps,
  initializeApp,
  FirebaseApp,
} from "firebase/app";
import {
  realtimeDb,
  auth,
  prodConfig,
  firebaseConfig,
} from "../firebaseConfig";
import notificationApi from "../api/notifications";
import { subscribeHospital, isInboxRealtimeDelivering } from "../realtime/realtimeClient";
import { REALTIME_EVENT } from "../realtime/realtimeTypes";
import { REALTIME_POLLING_ENABLED, REALTIME_REST_SYNC_ENABLED } from "../realtime/realtimeConfig";
import {
  joinApiUrl,
  resolveActiveHospitalId,
  resolveHospitalRequestAuth,
} from "../utils";
import { useSelector } from "react-redux";
import {
  clearHospitalUnreadCount,
  decrementHospitalUnreadCount,
  getHospitalUnreadCounts,
  normalizeHospitalId,
  patchHospitalUnreadCounts,
  setHospitalUnreadCount,
  subscribeHospitalUnreadCounts,
} from "../realtime/notificationCountsStore";

export type NotificationRole = "admin" | "hospital" | "doctor";

export interface AppliedShiftDay {
  shiftsDaysId?: string;
  shiftDate?: string;
  startTime?: number;
  endTime?: number;
  status?: string | number;
}

export interface RealtimeNotification {
  id: string;
  activityId: string; // The actual ID for API calls
  title: string;
  message: string;
  messageEventType?: string;
  timestamp: number;
  read: boolean;
  isRead: boolean; // API field name
  hospitalId?: string;
  hospitalName?: string;
  image?: string;
  doctorName?: string;
  doctorSpecialty?: string;
  shiftNo?: string;
  shiftId?: string;
  shiftsDaysId?: string;
  shiftDate?: string;
  appliedShiftDays?: AppliedShiftDay[];
  shiftSpecialtyName?: string;
  shiftSkillLevelName?: string;
  numberOfDaysApplied?: number | string;
  isFavoriteDoctor?: boolean;
  hasWorkedBefore?: boolean;
  onStaff?: boolean;
  preCredentialed?: boolean;
  relationshipKind?: string;
  relationshipLabel?: string;
  isShiftOpen?: boolean;
  status?: string;
  relatedId?: string;
  data?: Record<string, any>;
  doctorUserId?: string;
  ordersId?: string;
  /** Explicit shifts UUID from API (preferred over shiftId aliases). */
  shiftsId?: string;
}

export interface UseNotificationsResult {
  notifications: RealtimeNotification[];
  unreadCount: number;
  unreadCountsByHospital: Record<string, number>;
  loading: boolean;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  refetch: () => void;
  loadOlder: () => void;
  hasLoadedOlder: boolean;
}

// Simple hook that returns just notifications array (backward compatible)
export const useNotifications = (
  role?: NotificationRole,
  userId?: string | null,
  networkHospitalIds?: string[], // Optional: pass all hospital IDs for master view
): RealtimeNotification[] => {
  const result = useNotificationsAdvanced(role, userId, networkHospitalIds);
  return result.notifications;
};

// Advanced hook with full functionality
export const useNotificationsAdvanced = (
  role?: NotificationRole,
  userId?: string | null,
  networkHospitalIds?: string[], // Optional: pass all hospital IDs for master view
): UseNotificationsResult => {
  const [notifications, setNotifications] = useState<RealtimeNotification[]>(
    [],
  );
  const [unreadCountsByHospital, setUnreadCountsByHospital] = useState<
    Record<string, number>
  >(() => getHospitalUnreadCounts());

  useEffect(() => subscribeHospitalUnreadCounts(setUnreadCountsByHospital), []);

  const [loading, setLoading] = useState(true);
  const [fetchQueue, setFetchQueue] = useState<string[]>([]);
  const [initialFetchDone, setInitialFetchDone] = useState(false);
  const [fetchFullHistory, setFetchFullHistory] = useState(false);
  const lastFirebaseRawValues = useRef<Record<string, string>>({});

  // Fetch hospital list to access unique firebase tokens
  const hospitals = useSelector((state: any) => state.currentHospital.list);

  // A stringified hash to detect IF firebase tokens actually changed, preventing endless listener tearing/rebuilding
  const hospitalsConfigHash = useMemo(() => {
    return (hospitals || [])
      .map((h: any) => `${h.id}:${h.firebaseToken}`)
      .join("|");
  }, [hospitals]);

  // Determine which IDs to use for API calls
  const hospitalIdsForApi = networkHospitalIds?.length
    ? networkHospitalIds
    : userId
      ? [userId]
      : [];

  const activeHospitalId = useMemo(
    () =>
      normalizeHospitalId(
        resolveActiveHospitalId() || userId || hospitalIdsForApi[0] || "",
      ),
    [userId, hospitalIdsForApi.join(",")],
  );

  const unreadCount = activeHospitalId
    ? (unreadCountsByHospital[activeHospitalId] ?? 0)
    : 0;

  const resolveTimestamp = (value?: number) => {
    if (!value) return Date.now();
    return value < 1_000_000_000_000 ? value * 1000 : value;
  };

  // The backend writes the AUTHORITATIVE unread count to the hospital's Firebase
  // RTDB node as { unReadCount, time, type, notificationId } (sometimes JSON-encoded
  // as a string). Read it so the per-hospital badge is a SET, not a blind +1 that
  // drifts upward and bleeds across the master/child dropdown.
  const extractUnreadCount = (raw: any): number | null => {
    let v = raw;
    if (typeof v === "string") {
      try {
        v = JSON.parse(v);
      } catch {
        return null;
      }
    }
    if (v && typeof v === "object") {
      const c = v.unReadCount ?? v.unreadCount ?? v.badge ?? v.count;
      if (typeof c === "number" && Number.isFinite(c) && c >= 0) return c;
      if (typeof c === "string" && c.trim() !== "" && !Number.isNaN(Number(c))) {
        return Number(c);
      }
    }
    return null;
  };

  const normalizeList = (payload: any): any[] => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.list)) return payload.list;
    if (Array.isArray(payload?.records)) return payload.records;
    if (Array.isArray(payload?.items)) return payload.items;
    return [];
  };

  const buildId = () =>
    `notification-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  const toRealtimeNotification = (item: any): RealtimeNotification => {
    const activityId = String(
      item.activityId ||
        item.id ||
        item.notificationId ||
        item.messageId ||
        buildId(),
    );
    // Do NOT treat raw readTime (0 / epoch seconds) as truthy without the
    // threshold — Boolean(0) is fine, but Boolean(someTimestamp) true is OK.
    // Prefer explicit isRead/read from the API DTO.
    const isRead = Boolean(
      item.isRead === true ||
        item.read === true ||
        (typeof item.readTime === "number" && item.readTime > 0),
    );

    const parsedData = (() => {
      const raw = item.data || item.relatedData || {};
      if (typeof raw === "string") {
        try {
          return JSON.parse(raw);
        } catch {
          return {};
        }
      }
      return raw && typeof raw === "object" ? raw : {};
    })();

    const eventType = String(
      item.messageEventType || item.notificationType || item.type || "",
    ).toUpperCase();
    const relatedIsShift =
      !eventType.includes("EMPLOYMENT") &&
      !eventType.includes("LEAVE") &&
      !eventType.includes("LICENCE") &&
      !eventType.includes("ROSTER");

    const shiftsId = String(
      item.shiftsId ||
        item.shiftId ||
        parsedData.shiftsId ||
        parsedData.shiftId ||
        (relatedIsShift ? item.relatedId : "") ||
        "",
    ).trim();

    const shiftsDaysId = String(
      item.shiftsDaysId ||
        item.shiftDayId ||
        item.shiftDaysId ||
        parsedData.shiftsDaysId ||
        parsedData.shiftDayId ||
        parsedData.shiftDaysId ||
        (typeof parsedData.shiftsDaysIds === "string"
          ? String(parsedData.shiftsDaysIds).split(",")[0]?.trim()
          : "") ||
        (Array.isArray(item.appliedShiftDays) &&
        item.appliedShiftDays[0]?.shiftsDaysId
          ? item.appliedShiftDays[0].shiftsDaysId
          : "") ||
        "",
    ).trim();

    return {
      id: activityId,
      activityId: activityId,
      title: item.title || item.message || item.body || "Notification",
      message: item.message || item.body || item.mess || "",
      messageEventType:
        item.messageEventType ||
        item.notificationType ||
        item.type ||
        undefined,
      timestamp: resolveTimestamp(
        item.timestamp || item.createTime || item.time || Date.now(),
      ),
      read: isRead,
      isRead: isRead,
      hospitalId: item.hospitalId,
      hospitalName: item.hospitalName,
      // Parse image with fallback to various fields
      image: item.image || item.imageUrl || item.avatar || item.profilePic,
      // Prioritize specialty name over ID
      doctorName:
        item.doctorName ||
        item.userFullName ||
        item.firstName ||
        parsedData.doctorName,
      doctorSpecialty:
        item.specialtyName || item.doctorSpecialty || item.specialty,
      shiftNo: item.shiftNo,
      shiftsId: shiftsId || undefined,
      shiftId: shiftsId || undefined,
      shiftsDaysId: shiftsDaysId || undefined,
      // Ensure date is string
      shiftDate:
        item.shiftDate || (item.shiftDay ? String(item.shiftDay) : undefined),
      appliedShiftDays: item.appliedShiftDays || [],
      shiftSpecialtyName: item.shiftSpecialtyName,
      shiftSkillLevelName: item.shiftSkillLevelName,
      numberOfDaysApplied: item.numberOfDaysApplied,
      isFavoriteDoctor: Boolean(item.isFavoriteDoctor),
      hasWorkedBefore: Boolean(item.hasWorkedBefore),
      onStaff: Boolean(item.onStaff),
      preCredentialed: Boolean(item.preCredentialed),
      relationshipKind: item.relationshipKind || parsedData.relationshipKind,
      relationshipLabel: item.relationshipLabel || parsedData.relationshipLabel,
      isShiftOpen: Boolean(item.isShiftOpen),
      status: item.status || parsedData.status,
      relatedId: item.relatedId || shiftsId || undefined,
      data: parsedData,
      doctorUserId:
        item.doctorUserId ||
        parsedData.userId ||
        parsedData.doctorUserId ||
        undefined,
      ordersId:
        item.ordersId || parsedData.ordersId || parsedData.orderId || undefined,
    };
  };

  // Fetch notifications from API
  const fetchNotifications = useCallback(
    async (specificIds?: string[], forceFullHistory?: boolean) => {
      // If specificIds are provided, only fetch for those. Otherwise, fetch for all.
      const idsToFetch = specificIds?.length ? specificIds : hospitalIdsForApi;
      if (idsToFetch.length === 0) return;

      // Do not hit a protected route before login completes (avoids Token is null noise
      // and empty inbox while the badge still reflects Firebase unread counts).
      const authProbe = resolveHospitalRequestAuth(hospitalIdsForApi, userId);
      if (!authProbe.token) {
        setLoading(false);
        // One deferred retry once Sa-Token is stored after portal-login.
        window.setTimeout(() => {
          const retry = resolveHospitalRequestAuth(hospitalIdsForApi, userId);
          if (retry.token) {
            setInitialFetchDone(false);
            setFetchQueue((prev) =>
              prev.length ? prev : [...hospitalIdsForApi],
            );
          }
        }, 800);
        return;
      }

      // Full inbox on explicit history load; important-only is still useful for the
      // compact bell preview but must not hide the main list forever.
      const requestImportantOnly = !(forceFullHistory || fetchFullHistory);

      try {
        setLoading(true);
        /* console log removed */

        let responseData: any = null;

        try {
          // Centralized token/account selection (master token for multi-hospital
          // requests, child token for single-hospital). See resolveHospitalRequestAuth.
          const auth = resolveHospitalRequestAuth(hospitalIdsForApi, userId);
          const useMasterToken = auth.useMasterToken;
          const accountIdToUse = auth.accountId;
          // normalToken is still needed for the 9000x fallback below.
          let normalToken = localStorage.getItem("token-name");
          if (normalToken === "null") normalToken = null;

          if (!auth.token) {
            setLoading(false);
            return;
          }

          const url = joinApiUrl(
            process.env.REACT_APP_URL || "",
            "/admin/v1/hospital/analytics/notifications",
          );

          const headers: any = {
            "Content-Type": "application/json",
            "doctor-admin-token": auth.token,
            accountId: accountIdToUse,
          };

          if (!useMasterToken) {
            headers["X-Hospital-Id"] = accountIdToUse;
          }

          // Use native fetch to bypass the global axios interceptor that forces logout on 9000x errors
          let fetchRes = await fetch(url, {
            method: "POST",
            headers,
            body: JSON.stringify({
              hospitalIds: idsToFetch,
              importantOnly: requestImportantOnly,
            }),
          });

          responseData = await fetchRes.json();

          // If master token gave a 9000X error, fallback to normal token
          if (
            (responseData?.code === 90003 ||
              responseData?.code === 90000 ||
              responseData?.code === 90006) &&
            useMasterToken &&
            normalToken &&
            normalToken !== auth.token
          ) {
            /* console warn removed */
            // IMPORTANT: Do NOT delete master-token or persistent-parent-hospital from localStorage here!
            // Doing so will permanently destroy the user's dropdown hierarchy next time they naturally reload.
            // We just quietly fail over to the normal token for this specific background polling request.

            headers["doctor-admin-token"] = normalToken;
            headers["accountId"] = userId ?? hospitalIdsForApi[0];
            headers["X-Hospital-Id"] = userId ?? hospitalIdsForApi[0];

            fetchRes = await fetch(url, {
              method: "POST",
              headers,
              body: JSON.stringify({
                hospitalIds: idsToFetch,
                importantOnly: requestImportantOnly,
              }),
            });
            responseData = await fetchRes.json();
          }

          /*  console.log(
            "ðŸ”” Notifications API response secure fetch:",
            responseData,
          );*/
        } catch (fetchErr) {
          // Safely fallback if fetch fails completely
          const response = await notificationApi.getNotifications({
            hospitalIds: idsToFetch,
            importantOnly: requestImportantOnly,
            accountId: userId ?? hospitalIdsForApi[0],
          });
          responseData = (response as any)?.data;
        }

        // Check for successful response (code 0 or 200)
        const isSuccess =
          responseData?.code === 0 || responseData?.code === 200;

        if (!isSuccess) {
          setLoading(false);
          return;
        }

        const list = normalizeList(responseData?.data ?? responseData);

        // console.log("ðŸ”” Normalized notifications:", list);

        const mapped = list.map((item: any) => {
          // Force inject hospitalId if it's missing and we heavily imply it from specificIds
          const inferredHospId =
            item.hospitalId ||
            (idsToFetch.length === 1 ? idsToFetch[0] : undefined);
          return toRealtimeNotification({
            ...item,
            hospitalId: inferredHospId,
          });
        });

        const isPartialFetch =
          specificIds &&
          specificIds.length > 0 &&
          specificIds.length < hospitalIdsForApi.length;

        const finalCombinedList: RealtimeNotification[] = [];

        setNotifications((prev) => {
          const mappedIds = new Set(mapped.map((m) => m.id));

          let kept: RealtimeNotification[] = [];
          if (isPartialFetch) {
            kept = prev.filter((n) => {
              const isOtherHospital =
                !n.hospitalId || !specificIds.includes(n.hospitalId);
              const isOptimistic =
                !mappedIds.has(n.id) && Date.now() - n.timestamp < 10000;
              return isOtherHospital || isOptimistic;
            });
          } else {
            kept = prev.filter(
              (n) => !mappedIds.has(n.id) && Date.now() - n.timestamp < 10000,
            );
          }

          const combinedMap = new Map<string, RealtimeNotification>();
          [...kept, ...mapped].forEach((item) => {
            if (!combinedMap.has(item.id)) {
              const existedPrev = prev.find((p) => p.id === item.id);
              if (existedPrev?.isRead) {
                item.isRead = true;
                item.read = true;
              }
              combinedMap.set(item.id, item);
            }
          });

          const nextList = Array.from(combinedMap.values()).sort(
            (a, b) => b.timestamp - a.timestamp,
          );
          if (
            nextList.some((item) => {
              const type = String(item.messageEventType || "").toUpperCase();
              return (
                type.includes("EMPLOYMENT_CLAIMED") ||
                type.includes("STAFF_LICENCE")
              );
            })
          ) {
            window.dispatchEvent(new Event("workforce-changed"));
          }

          return nextList;
        });

        // Reconcile badge with the same allowlisted unread total the inbox uses.
        // The list API soft-deletes stale NEW_APPLICATION rows; prefer that truth
        // when we fetched the full allowlist so dropdown badges can't stay high
        // over an empty inbox.
        try {
          if (idsToFetch.length === 1) {
            const hospitalId = normalizeHospitalId(idsToFetch[0]);
            const unreadFromList = mapped.filter(
              (n) =>
                !n.isRead && normalizeHospitalId(n.hospitalId) === hospitalId,
            ).length;
            if (hospitalId && !requestImportantOnly) {
              setHospitalUnreadCount(hospitalId, unreadFromList);
            } else if (hospitalId) {
              const countRes = await notificationApi.getUnreadCount({
                hospitalIds: idsToFetch,
                importantOnly: false,
                accountId: userId ?? idsToFetch[0],
              });
              const body = (countRes as any)?.data ?? countRes;
              const isCountSuccess =
                body?.code === 0 || body?.code === 200 || body?.status === 200;
              if (isCountSuccess) {
                const raw =
                  body?.data?.unreadCount ??
                  body?.data?.unReadCount ??
                  body?.data ??
                  0;
                const nextCount = Math.max(0, Math.floor(Number(raw) || 0));
                // Empty important-only preview + zero API count → clear ghosts.
                if (mapped.length === 0 && nextCount === 0) {
                  setHospitalUnreadCount(hospitalId, 0);
                } else {
                  setHospitalUnreadCount(hospitalId, nextCount);
                }
              } else {
                setHospitalUnreadCount(hospitalId, unreadFromList);
              }
            }
          } else if (idsToFetch.length > 1) {
            const patch: Record<string, number> = {};
            idsToFetch.forEach((id) => {
              const key = normalizeHospitalId(id);
              if (!key) return;
              patch[key] = mapped.filter(
                (n) => !n.isRead && normalizeHospitalId(n.hospitalId) === key,
              ).length;
            });
            patchHospitalUnreadCounts(patch, { replaceFetched: idsToFetch });
          }
        } catch {
          // Non-fatal: keep previous badge if reconcile fails.
        }
      } catch (error) {
        console.error("Failed to fetch notifications", error);
      } finally {
        setLoading(false);
      }
    },
    [hospitalIdsForApi.join(","), userId, fetchFullHistory],
  );

  // Mark single notification as read
  const markAsRead = useCallback(
    async (notificationId: string) => {
      let targetHospitalId = "";
      setNotifications((prev) => {
        const target = prev.find(
          (n) =>
            n.activityId === notificationId || n.id === notificationId,
        );
        targetHospitalId = normalizeHospitalId(
          target?.hospitalId || userId || hospitalIdsForApi[0],
        );
        if (targetHospitalId) {
          decrementHospitalUnreadCount(targetHospitalId, 1);
        }
        return prev.map((n) =>
          n.activityId === notificationId || n.id === notificationId
            ? { ...n, isRead: true, read: true }
            : n,
        );
      });

      window.dispatchEvent(
        new CustomEvent("notifications-update", {
          detail: {
            action: "mark-read",
            notificationId,
            hospitalId: targetHospitalId || userId,
          },
        }),
      );

      try {
        const response = await notificationApi.markAsRead({
          notificationId,
          accountId: userId ?? hospitalIdsForApi[0],
        });

        if (
          (response as any)?.data?.code !== 0 &&
          (response as any)?.status !== 200
        ) {
          /* console warn removed */
        }
      } catch (error) {
        console.error("Failed to mark notification as read", error);
      }
    },
    [userId, hospitalIdsForApi],
  );

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    // OPTIMISTIC UPDATE: Assume success immediately for better UX
    setNotifications((prev) =>
      prev.map((n) => ({ ...n, isRead: true, read: true })),
    );

    const targetId = normalizeHospitalId(userId ?? hospitalIdsForApi[0]);
    if (targetId) {
      clearHospitalUnreadCount(targetId);
    } else {
      hospitalIdsForApi.forEach((id) => clearHospitalUnreadCount(id));
    }
    // Signal other components to clear their badges immediately
    window.dispatchEvent(
      new CustomEvent("notifications-update", {
        detail: { action: "mark-all-read", hospitalId: userId },
      }),
    );

    try {
      /* console log removed */
      const response = await notificationApi.markAllAsRead({
        hospitalIds: hospitalIdsForApi,
        accountId: userId ?? hospitalIdsForApi[0],
      });

      // If backend fails, we technically should revert, but for "read status" errors are rare
      // and revert is jarring. We'll just log any errors.
      if (
        (response as any)?.data?.code !== 0 &&
        (response as any)?.status !== 200
      ) {
        /* console warn removed */
      }
    } catch (error) {
      console.error(
        "Failed to mark all notifications as read (UI updated optimistically)",
        error,
      );
    }
  }, [hospitalIdsForApi, userId]);

  useEffect(() => {
    // Reset initial fetch done when IDs change so we re-fetch for new hospital
    setInitialFetchDone(false);
  }, [hospitalIdsForApi.join(",")]);

  // 1. Authenticate & Listen to Firebase Realtime Database
  useEffect(() => {
    let isMounted = true;

    if (!role || hospitalIdsForApi.length === 0) {
      setNotifications([]);
      setLoading(false);
      return;
    }

    const unsubscribers: (() => void)[] = [];
    const activeApps: string[] = [];

    const initializeFirebase = async () => {
      // Trigger fetch IMMEDIATELY on load to show content without waiting for auth
      // This solves the issue where "default" account sees nothing if firebase is slow/fails
      if (!initialFetchDone) {
        /* console log removed */
        fetchNotifications();
        if (isMounted) setInitialFetchDone(true);
      }

      // Helper to initialize a specific app instance (handling Dev vs Prod config)
      const initHospitalConnection = async (
        appName: string,
        token: string,
      ): Promise<Database | null> => {
        if (!isMounted) return null;
        let app: FirebaseApp;
        try {
          app = getApp(appName);
        } catch {
          // Try with standard config first
          app = initializeApp(firebaseConfig, appName);
        }
        activeApps.push(appName);

        const appAuth = getAuth(app);
        const appDb = getDatabase(app);

        try {
          if (!appAuth.currentUser) {
            /* console log removed */
            await signInWithCustomToken(appAuth, token);
            /* console log removed */
          } else {
            // Already authenticated by another hook instance
            /* console log removed */
          }
          return appDb;
        } catch (error: any) {
          /* console warn removed */

          // FALLBACK: Try Production Config if mismatch
          if (
            error.code === "auth/configuration-not-found" ||
            error.code === "auth/project-not-found"
          ) {
            /* console log removed */
            try {
              // We need a unique name for the fallback app too to avoid collision if the original app stays around
              // Actually, we can just delete the old app, or use a new name
              const prodAppName = `${appName}-prod`;
              let prodApp;
              try {
                prodApp = getApp(prodAppName);
              } catch {
                if (prodConfig)
                  prodApp = initializeApp(prodConfig, prodAppName);
              }

              if (prodApp) {
                activeApps.push(prodAppName);
                const prodAuth = getAuth(prodApp);
                const prodDb = getDatabase(prodApp);
                await signInWithCustomToken(prodAuth, token);
                /* console log removed */
                return prodDb;
              }
            } catch (e) {
              console.error(`âŒ [${appName}] Fallback also failed:`, e);
            }
          }
          return null;
        }
      };

      // Extract master fallback tokens once before the loop
      let masterFirebaseToken: string | null = null;
      try {
        const rawParent = localStorage.getItem("persistent-parent-hospital");
        if (rawParent) {
          masterFirebaseToken = JSON.parse(rawParent)?.firebaseToken;
        }
      } catch (e) {}

      // Process each hospital ID
      for (const hId of hospitalIdsForApi) {
        if (!isMounted) break;

        // 1. Try to find unique token from Redux list
        // We cast to 'any' because strict typing of Redux state isn't set up fully here
        const hospitalData = (hospitals as any[])?.find(
          (h: any) => h.id === hId,
        );
        const uniqueToken = hospitalData?.firebaseToken;

        let targetDb: Database | null = null;
        const path = `account/${hId}/notification`;

        // The Master firebase token has permission to read ALL children paths.
        // If a child token is missing or if we are a child trying to read a sibling,
        // we use the master token to bypass Firebase permissions errors.
        const tokenToUse =
          uniqueToken ||
          masterFirebaseToken ||
          localStorage.getItem("firebase-token");

        if (tokenToUse) {
          targetDb = await initHospitalConnection(
            `app-conn-${hId}`,
            tokenToUse,
          );
        } else if (auth.currentUser) {
          // Last resort: use the existing global auth instance (likely Dev)
          targetDb = realtimeDb;
        }

        if (isMounted && targetDb) {
          try {
            // Check if app is not deleted before creating ref
            // Accessing app property safely if possible, or just try-catch the ref creation
            /* console log removed */
            const dbRef = ref(targetDb, path);
            const unsub = onValue(
              dbRef,
              (snapshot) => {
                if (!isMounted) return;
                const val = snapshot.val();
                if (val) {
                  const valString = JSON.stringify(val);
                  const isFirstLoad = !(hId in lastFirebaseRawValues.current);

                  // Only react if Firebase actually changed the data.
                  // This breaks infinite loops if the backend is doing sync-writes back to Firebase while we read.
                  if (lastFirebaseRawValues.current[hId] !== valString) {
                    lastFirebaseRawValues.current[hId] = valString;

                    // Always re-verify against allowlisted API count. Legacy writers
                    // still hard-coded unReadCount=1 for chat/messages, which painted
                    // every hospital in the dropdown with a sticky ghost badge.
                    void (async () => {
                      try {
                        const countRes = await notificationApi.getUnreadCount({
                          hospitalIds: [hId],
                          importantOnly: false,
                          accountId: hId,
                        });
                        const body = (countRes as any)?.data ?? countRes;
                        const ok =
                          body?.code === 0 ||
                          body?.code === 200 ||
                          body?.status === 200;
                        if (ok) {
                          const raw =
                            body?.data?.unreadCount ??
                            body?.data?.unReadCount ??
                            body?.data ??
                            0;
                          setHospitalUnreadCount(
                            hId,
                            Math.max(0, Math.floor(Number(raw) || 0)),
                          );
                          return;
                        }
                      } catch {
                        // Ignore — do not fall back to RTDB. Legacy writers
                        // hard-coded unReadCount and re-poisoned cleared badges.
                      }
                    })();

                    // Refresh the actual notification LIST only for the hospital the
                    // user is currently viewing, and only after the first boot ping
                    // (the hook already fetches on mount). Other hospitals just update
                    // their badge from the authoritative count above — no list refetch.
                    const activeId =
                      resolveActiveHospitalId() || hospitalIdsForApi[0];
                    if (
                      !isFirstLoad &&
                      REALTIME_REST_SYNC_ENABLED &&
                      String(hId) === String(activeId)
                    ) {
                      setFetchQueue((prev) => [...prev, hId]);
                    }
                  }
                }
              },
              (err) => {
                const errText = String(
                  (err as any)?.message || (err as any)?.code || err || "",
                ).toLowerCase();
                const isExpectedAuthIssue =
                  errText.includes("permission") ||
                  errText.includes("auth") ||
                  errText.includes("token") ||
                  errText.includes("denied");

                if (!isExpectedAuthIssue) {
                  console.error(`Notification listener error [${hId}]:`, err);
                }
              },
            );
            unsubscribers.push(unsub);
          } catch (e) {
            /* console warn removed */
          }
        } else {
          // No valid Firebase auth/token for this hospital.
          // Realtime is skipped silently and the existing API polling fallback continues to work.
        }
      }
    };

    // Run initialization
    initializeFirebase();

    // Safety-net poll when WebSocket is down — keep short so applications/messages
    // still surface within ~15s instead of multi-minute lag.
    const intervalId = REALTIME_POLLING_ENABLED
      ? setInterval(() => {
          if (isMounted && !isInboxRealtimeDelivering()) {
            setFetchQueue((prev) => [...prev, ...hospitalIdsForApi]);
          }
        }, 15_000)
      : undefined;

    return () => {
      isMounted = false;
      unsubscribers.forEach((unsub) => unsub());
      if (intervalId !== undefined) {
        clearInterval(intervalId);
      }

      // Clean up dynamic apps to prevent memory leaks and config conflicts
      // IMPORTANT: DO NOT delete the Firebase Apps here!
      // Since NotificationPanel and TopNav BOTH use this hook simultaneously,
      // if one unmounts and deletes the app, it breaks the other's real-time listeners!
      // activeApps.forEach((name) => {
      //   try {
      //     if (name !== "[DEFAULT]") {
      //       try {
      //         const app = getApp(name);
      //         // deleteApp(app).catch(() => {});
      //       } catch (e) {}
      //     }
      //   } catch {}
      // });
    };
  }, [role, hospitalIdsForApi.join(","), hospitalsConfigHash]); // Depend on hashed config so we only re-run if tokens legit changed

  // STOMP WebSocket — subscribe to EACH hospital in scope so the group/dropdown
  // can learn which hospitals have new notifications WITHOUT refetching every
  // hospital's full notification list. The active hospital (the list the user is
  // actually viewing) still does a REST refetch so its items stay in sync; the
  // other hospitals just bump their own unread badge from the live signal.
  useEffect(() => {
    const ids = (
      hospitalIdsForApi.length > 0
        ? hospitalIdsForApi
        : [resolveActiveHospitalId()].filter(Boolean)
    ) as string[];
    if (ids.length === 0) return;

    const activeId = resolveActiveHospitalId() || ids[0];

    const unsubscribers = ids.map((hId) =>
      subscribeHospital(hId, (event) => {
        const type = String(event.type || "");
        const isNotification =
          type === REALTIME_EVENT.NOTIFICATION_CREATED ||
          type === REALTIME_EVENT.NOTIFICATION_UNREAD_COUNT;
        const isConversation =
          type === REALTIME_EVENT.CONVERSATION_MESSAGE_CREATED;
        if (!isNotification && !isConversation) return;

        // NOTE: We deliberately do NOT optimistically increment the badge here.
        // The authoritative per-hospital unread count comes from the Firebase RTDB
        // node (see the onValue listener above), which the backend updates to the
        // exact DB count on every notification. Blindly doing +1 on each STOMP
        // signal previously caused counts to drift upward (e.g. showing 9 for 1)
        // and to bleed across hospitals in the master dropdown, because the same
        // logical notification can arrive via multiple signals and never reconciled
        // for non-active hospitals.

        // Only the hospital the user is actively viewing needs its full list
        // refreshed so new rows appear; its badge is still reconciled by the
        // authoritative count. Other hospitals stay badge-only via Firebase.
        if ((isNotification || isConversation) && REALTIME_REST_SYNC_ENABLED && hId === activeId) {
          setFetchQueue((prev) => [...prev, hId]);
        }
      }),
    );

    return () => {
      unsubscribers.forEach((unsub) => unsub());
    };
  }, [hospitalIdsForApi.join(",")]);

  // 2. Fetch notifications from API when signal changes or initially
  useEffect(() => {
    // Listener for manual updates from other components
    const handleManualUpdate = (e: Event) => {
      const detail = (e as CustomEvent).detail;

      if (detail?.action === "mark-all-read") {
        const targetId = normalizeHospitalId(detail.hospitalId);
        setNotifications((prev) => {
          if (targetId) {
            return prev.map((n) =>
              !n.hospitalId ||
              normalizeHospitalId(n.hospitalId) === targetId
                ? { ...n, isRead: true, read: true }
                : n,
            );
          }
          return prev.map((n) => ({ ...n, isRead: true, read: true }));
        });
      } else if (detail?.action === "mark-read" && detail.notificationId) {
        setNotifications((prev) =>
          prev.map((n) =>
            n.activityId === detail.notificationId ||
            n.id === detail.notificationId
              ? { ...n, isRead: true, read: true }
              : n,
          ),
        );
      }

      // Only refetch for external signals — not after our own optimistic mark-read,
      // which would race the backend and briefly restore the unread badge.
      if (
        detail?.action !== "mark-read" &&
        detail?.action !== "mark-all-read"
      ) {
        setTimeout(() => fetchNotifications(), 2000);
      }
    };
    window.addEventListener("notifications-update", handleManualUpdate);

    return () => {
      window.removeEventListener("notifications-update", handleManualUpdate);
    };
  }, [fetchNotifications]);

  // 3. Main Fetch Effect
  useEffect(() => {
    // ALWAYS fetch initially if we have valid IDs, regardless of fetchQueue
    if (hospitalIdsForApi.length === 0) return;

    // If fetch hasn't happened yet for these IDs, do it now
    if (!initialFetchDone) {
      fetchNotifications();
      setInitialFetchDone(true);
    }
    // Otherwise only if there's something in the queue
    else if (fetchQueue.length > 0 && REALTIME_REST_SYNC_ENABLED) {
      // Dedup the queue
      const uniqueIds = Array.from(new Set(fetchQueue));

      // Fast debounce so new applications show within ~half a second of the
      // Firebase/WebSocket ping (previously 1500ms plus a 5 minute safety poll).
      const timeoutId = setTimeout(() => {
        // ALWAYS fetch ALL hospital IDs if anything changes anywhere.
        // Partial fetches (uniqueIds) will cause the backend API to silently return empty lists
        // for unselected hospitals due to master-token filtering constraints.
        fetchNotifications();
        setFetchQueue([]);
      }, 400);
      return () => clearTimeout(timeoutId);
    }
  }, [
    fetchQueue,
    initialFetchDone,
    fetchNotifications,
    hospitalIdsForApi.join(","),
  ]);

  const loadOlder = useCallback(() => {
    if (!fetchFullHistory) {
      setFetchFullHistory(true);
      fetchNotifications(undefined, true);
    }
  }, [fetchFullHistory, fetchNotifications]);

  return {
    notifications,
    unreadCount,
    unreadCountsByHospital,
    loading,
    markAsRead,
    markAllAsRead,
    refetch: fetchNotifications,
    loadOlder,
    hasLoadedOlder: fetchFullHistory,
  };
};
